import * as dbAccessor from '@/utils/dbAccessor'
import {Logger} from '@/utils/tools'

function updateInventoryPromise (items, warehouseKey, warehouseName, warehouseSite, siteName, isQuantityReverse = false, isSelfStorage = false) {
  let productSum = items.reduce((sum, item) => {
    let {upc, organizationKey = ''} = item
    item.isAddedToInventoryOrigin = item.isAddedToInventory
    item.isAddedToInventory = false
    if (!organizationKey) {
      return sum
    }
    if (!sum[organizationKey]) {
      sum[organizationKey] = {}
    }
    if (!sum[organizationKey][upc]) {
      sum[organizationKey][upc] = {
        quantity: 0,
        warehouseSite: '',
        siteName: '',
        warehouseKey: '',
        items: []
      }
    }

    sum[organizationKey][upc].quantity = (sum[organizationKey][upc].quantity + (isQuantityReverse ? -1 : 1) * item.quantity)
    sum[organizationKey][upc].warehouseSite = item.warehouseSite || warehouseSite
    sum[organizationKey][upc].warehouseName = warehouseName || ''
    sum[organizationKey][upc].siteName = item.siteName || siteName || ''
    sum[organizationKey][upc].warehouseKey = warehouseKey
    sum[organizationKey][upc].forecastDocKey = item.forecastDocKey || ''
    sum[organizationKey][upc].tracking = item.trackings?.[0] || ''
    sum[organizationKey][upc].items.push(item)
    return sum
  }, {})

  return Object.keys(productSum).reduce((promiseArr, organizationKey) => {
    let perOrganizationArr = Object.keys(productSum[organizationKey]).map(upc => {
      let predicates = [
        {
          field: `upc`,
          compare: '==',
          value: upc
        }
      ]
      let change = productSum[organizationKey][upc]
      return dbAccessor.queryWithPredicatesStore(predicates, 'tenants', organizationKey, 'inventory')
        .then(docs => {
          return dbAccessor.runInTransactionStore(transaction => {
            let warehouseInventoryKey = `${change.warehouseSite}_${organizationKey}`
            if (isQuantityReverse ? !change.items[0].isAddedToInventoryOrigin : docs.size === 0) {
              let warehouseInventoryGetter = transaction.get(dbAccessor.buildStoreQuery(['warehouses', change.warehouseKey, 'inventory', warehouseInventoryKey]))
              let forecastDoc = (change.forecastDocKey && !isQuantityReverse) && transaction.get(dbAccessor.buildStoreQuery(['forecasts', change.forecastDocKey]))
              let promises = [warehouseInventoryGetter]
              forecastDoc && promises.push(forecastDoc)
              return Promise.all(promises)
                .then(docArray => {
                  let [warehouseInventoryDoc, forecastDoc] = docArray
                  forecastDoc && updateForecast(transaction, forecastDoc, change, upc)
                  updateWarehouseInventory(transaction, warehouseInventoryDoc, change, upc)
                  // no need to update tenant inventory and package
                })
            } else {
              if (docs.size === 0) throw Error('Organization product missing.')
              let productGetter = transaction.get(dbAccessor.buildStoreQuery(['tenants', organizationKey, 'inventory', docs.docs[0].id]))
              let warehouseInventoryGetter = !isSelfStorage && transaction.get(dbAccessor.buildStoreQuery(['warehouses', change.warehouseKey, 'inventory', warehouseInventoryKey]))
              let forecastDoc = (change.forecastDocKey && !isQuantityReverse) && transaction.get(dbAccessor.buildStoreQuery(['forecasts', change.forecastDocKey]))
              const promiseList = warehouseInventoryGetter ? [productGetter, warehouseInventoryGetter] : [productGetter]
              forecastDoc && promiseList.push(forecastDoc)
              return Promise.all(promiseList)
                .then(docArray => {
                  let [organizationInventoryDoc, warehouseInventoryDoc, forecastDoc] = docArray
                  forecastDoc && updateForecast(transaction, forecastDoc, change, upc)
                  updateOrganizationInventory(transaction, organizationInventoryDoc, change, isSelfStorage)
                  isSelfStorage || updateWarehouseInventory(transaction, warehouseInventoryDoc, change, upc)
                  
                  change.items.forEach(item => { 
                    item.isAddedToInventory = true 
                  })
                  return 'success'
                })
            }
          })
            .catch((error) => {
              change.items.forEach(item => { 
                item.isAddedToInventory = item.isAddedToInventoryOrigin 
              })
              throw error
            })
        })
    })
    return [...promiseArr, ...perOrganizationArr]
  }, [])
}

function updateOrganizationInventory (transaction, organizationInventoryDoc, change, isSelfStorage) {
  if (organizationInventoryDoc.exists) {
    let product = organizationInventoryDoc.data()
    let productId = organizationInventoryDoc.id
    let addressEncode = isSelfStorage ? `warehouse${productId}${Buffer.from(change.siteName).toString('base64')}` : Buffer.from('warehouse' + productId + change.warehouseSite)
    let distribution = product.distribution ? product.distribution : {}

    let oldValue = (distribution[addressEncode] && distribution[addressEncode].quantity) || 0
    distribution[addressEncode] = isSelfStorage ? {
      uid: change.warehouseKey,
      userName: 'self',
      warehouseSite: `self-${change.siteName}`,
      siteName: change.siteName,
      quantity: oldValue + change.quantity
    } : {
      uid: change.warehouseKey,
      userName: change.warehouseName,
      warehouseSite: change.warehouseSite,
      siteName: change.siteName || change.warehouseSite,
      quantity: oldValue + change.quantity,
      warehouseKey: change.warehouseKey
    }

    let quantity = product['quantity'] ? product['quantity'] + change.quantity : change.quantity
    let inbound = product['inbound'] ? product['inbound'] + change.quantity : change.quantity
    let newValue = {
      distribution,
      quantity,
      inbound
    }
    transaction.update(organizationInventoryDoc.ref, dbAccessor.addUpdateDocTimestamp(newValue))
  }
}

function updateWarehouseInventory (transaction, warehouseInventoryDoc, change, upc) {
  if (warehouseInventoryDoc.exists) {
    let {distribution} = warehouseInventoryDoc.data()
    if (distribution && distribution[upc]) {
      distribution[upc] += change.quantity
    } else if (!distribution) {
      distribution = {
        [upc]: change.quantity
      }
    } else {
      distribution[upc] = change.quantity
    }
    if (distribution[upc] === 0) delete distribution[upc]
    if (Object.keys(distribution).length >= 3000) {
      Logger.critical('warehouseHelper', {msg: 'distribution-size-exceed', change, upc, inventoryId: warehouseInventoryDoc.id})
      throw Error('distribution-size-exceed')
    }
    transaction.update(warehouseInventoryDoc.ref, dbAccessor.addUpdateDocTimestamp({distribution}))
  } else {
    let inventoryData = {
      tenantKey: change.items[0].organizationKey,
      siteKey: change.warehouseSite,
      distribution: {
        [upc]: change.quantity
      }
    }
    transaction.set(warehouseInventoryDoc.ref, dbAccessor.addNewDocTimestamp(inventoryData))
  }
}

function updateForecast (transaction, forecastDoc, change, upc) {
  if (forecastDoc.exists) {
    let isForecasted = false
    let { packages = {}, status } = forecastDoc.data()
    let forecastPkg = packages[change.tracking]
    if (Array.isArray(forecastPkg)) {
      forecastPkg.forEach(pkg => {
        if (pkg.upc === upc) {
          pkg.inboundQty = change.quantity
          change.quantity && (isForecasted = true)
        }
      })
      let isPartial = isForecasted && Object.keys(packages).some(key => {
        return packages[key].some(pkg => !pkg.inboundQty || pkg.inboundQty !== pkg.forecastQty)
      })
      transaction.update(forecastDoc.ref, dbAccessor.addNewDocTimestamp({
        packages,
        status: !isForecasted ? status : isPartial ? 'partial' : 'done'
      }))
    }
  }
}

function getRatesConfig (organizations, organizationKey, defaultRates) {
  if (!defaultRates) {
    throw Error('defaultRates is required')
  }
  
  let rates = defaultRates
  let organization = organizations.find(org => org.key === organizationKey)
  if (organization && organization.rates && organization.rates?.isEnabled) {
    rates = organization.rates
  }
  return rates
}

export {updateInventoryPromise, getRatesConfig}



// WEBPACK FOOTER //
// ./src/store/warehouse/warehouseHelper.js